import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { Route as RouteIcon, Building2, CalendarDays, FileText, Users, Plus, ArrowRight } from "lucide-react";

const kinds = [
  { key: "routes", label: "Rotas", icon: RouteIcon, to: "/admin/rotas/nova" },
  { key: "cities", label: "Cidades", icon: Building2, to: "/admin/cidades/nova" },
  { key: "events", label: "Eventos", icon: CalendarDays, to: "/admin/eventos/novo" },
  { key: "blog", label: "Blog", icon: FileText, to: "/admin/blog/novo" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { adminApi.get("/admin/stats").then(r => setStats(r.data)); }, []);

  return (
    <div data-testid="admin-dashboard" className="space-y-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 text-[#C6A762] mb-3">
            <span className="gold-divider" />
            <span className="text-[11px] tracking-[0.35em] uppercase">Visão Geral</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl">Dashboard</h1>
          <p className="text-[#B8B8B8] mt-2 text-sm">Bem-vindo ao painel administrativo da ROTTA.</p>
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {kinds.map((k) => (
          <StatCard
            key={k.key}
            label={k.label}
            value={stats?.counts?.[k.key] ?? "—"}
            icon={k.icon}
            createTo={k.to}
          />
        ))}
      </div>

      {stats && (
        <div className="grid lg:grid-cols-3 gap-6">
          <LatestPanel title="Últimas Rotas" items={stats.latest.routes} viewTo="/admin/rotas" editBase="/admin/rotas" />
          <LatestPanel title="Últimos Artigos" items={stats.latest.blog.map(b => ({ ...b, name: b.title }))} viewTo="/admin/blog" editBase="/admin/blog" />
          <LatestPanel title="Últimos Eventos" items={stats.latest.events} viewTo="/admin/eventos" editBase="/admin/eventos" />
        </div>
      )}

      <div className="card-rotta p-6 flex items-center justify-between gap-4 bg-[#141414]">
        <div className="flex items-center gap-3 text-sm">
          <Users size={16} className="text-[#C6A762]" />
          <span className="text-[#B8B8B8]">Inscritos na newsletter</span>
          <span className="font-display text-lg text-white">{stats?.counts?.subscribers ?? "—"}</span>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, createTo }) {
  return (
    <div className="card-rotta p-6 bg-[#141414] flex flex-col" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div className="w-10 h-10 rounded-full grid place-items-center bg-[#C6A762]/10 text-[#C6A762] border border-[#C6A762]/30">
          <Icon size={16} />
        </div>
        <Link to={createTo} className="text-[#C6A762] hover:text-[#D8BC72] p-1.5 rounded-full border border-[#C6A762]/30" title={`Novo ${label}`}>
          <Plus size={14} />
        </Link>
      </div>
      <div className="mt-6 text-[10px] tracking-[0.3em] uppercase text-[#7d7d7d]">{label}</div>
      <div className="font-display text-4xl text-white mt-1">{value}</div>
    </div>
  );
}

function LatestPanel({ title, items, viewTo, editBase }) {
  return (
    <div className="card-rotta p-6 bg-[#141414]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display tracking-wider uppercase text-sm">{title}</h3>
        <Link to={viewTo} className="text-[10px] tracking-[0.2em] uppercase text-[#C6A762] inline-flex items-center gap-1">
          Ver todos <ArrowRight size={12} />
        </Link>
      </div>
      <ul className="divide-y divide-[#1f1f1f]">
        {items.length === 0 && <li className="text-xs text-[#7d7d7d] py-3">Nenhum item ainda.</li>}
        {items.map((it) => (
          <li key={it.id} className="py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm text-white truncate">{it.name}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#7d7d7d] mt-0.5">
                {it.state || it.city || (it.date && new Date(it.date + "T12:00:00").toLocaleDateString("pt-BR"))}
              </div>
            </div>
            <Link to={`${editBase}/${it.id}`} className="text-[10px] tracking-[0.2em] uppercase text-[#C6A762] hover:translate-x-1 transition-transform">
              Editar →
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
